import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import Switch from '../../../../molecules/Switch'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import { CONTACT_INFO_FIELDS } from './WealthboxContactPanel'
import { HOUSEHOLD_TITLES } from './useWealthboxContactInfo'

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

const styleVariantOptions = [
  { label: 'Default (Medium)', value: 'default' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const FIELD_GROUP_ORDER = ['Identity', 'Contact', 'Professional', 'Personal']

// Wealthbox household titles are a fixed enum, NOT firm-defined relationship
// types as the SMT-1356 ticket claimed. There is no /v1/relationship_types
// endpoint - verified against live dev.wealthbox.com docs. See HOUSEHOLD_TITLES
// in useWealthboxContactInfo for the source of truth.
const householdTitleOptions = HOUSEHOLD_TITLES.map(t => ({ label: t, value: t }))

function WealthboxContactInfoConfigEditor ({ fieldPrefix }) {
  const { control, watch } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`
  const hideTitleValue = watch(`${configPrefix}.hideTitle`)
  const hideIconValue = watch(`${configPrefix}.hideIcon`)

  return (
    <Grid container spacing={2}>
      {/* Core */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Core</div>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideTitle`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show title'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideTitleValue && (
            <Grid item xs={6}>
              <Controller
                name={`${configPrefix}.title`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Title'>
                    <SydInput {...field} value={field.value || ''} placeholder='Family' minWidth='0' />
                  </SydLabel>
                )}
              />
            </Grid>
          )}
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideIcon`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show icon'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideIconValue && (
            <Grid item xs={2}>
              <Controller
                name={`${configPrefix}.icon`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Icon'>
                    <IconPickerField
                      value={field.value || 'wealthboxSmall'}
                      onChange={field.onChange}
                      placeholder='wealthboxSmall'
                    />
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={4}>
        <Controller
          name={`${fieldPrefix}.styleVariant`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Style Variant'>
              <SydSelect
                options={styleVariantOptions}
                value={field.value || 'default'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* Capabilities */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Capabilities</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.enableEdit`}
          control={control}
          render={({ field }) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
              <span className='font-size-sm'>Allow editing</span>
              <Switch
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            </div>
          )}
        />
      </Grid>

      {/* Filtering */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Filtering</div>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.householdTitles`}
          control={control}
          render={({ field }) => (
            <SydLabel
              label='Household Titles'
              description='Only household members with these titles will be shown as buttons. Leave empty to include all household members.'
            >
              <SydSelect
                multiple
                options={householdTitleOptions}
                value={field.value || []}
                onChange={(e) => {
                  const v = e.target.value
                  field.onChange(Array.isArray(v) ? v.filter(Boolean) : [])
                }}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* Visible fields */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Visible Contact Fields</div>
      </Grid>
      {FIELD_GROUP_ORDER.map(group => {
        const fields = CONTACT_INFO_FIELDS.filter(f => f.group === group)
        return (
          <Grid item xs={12} sm={6} key={group}>
            <div style={{ ...sectionHeading, borderBottom: 'none', marginBottom: '4px' }}>{group}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {fields.map(field => (
                <Controller
                  key={field.key}
                  name={`${configPrefix}.visibleContactFields`}
                  control={control}
                  render={({ field: rhf }) => {
                    const selected = Array.isArray(rhf.value) ? rhf.value : []
                    // Empty = show all (per AC). Default toggle state when
                    // empty: checked. When admin starts ticking boxes the
                    // list becomes the explicit allow-list.
                    const isChecked = selected.length === 0 || selected.includes(field.key)
                    return (
                      <label className='flex items-center gap-2' style={{ cursor: 'pointer' }}>
                        <Switch
                          checked={isChecked}
                          onChange={(e) => {
                            const checked = e.target.checked
                            // If the list is currently empty (= all), seed
                            // with all field keys minus the one being
                            // unchecked. Otherwise toggle membership.
                            const all = CONTACT_INFO_FIELDS.map(f => f.key)
                            const base = selected.length === 0 ? all : selected
                            const next = checked
                              ? Array.from(new Set([...base, field.key]))
                              : base.filter(k => k !== field.key)
                            // If the user has re-checked every field, fall
                            // back to empty to mean "all" (cleaner config).
                            rhf.onChange(next.length === all.length ? [] : next)
                          }}
                        />
                        <span className='font-size-sm'>{field.label}</span>
                      </label>
                    )
                  }}
                />
              ))}
            </div>
          </Grid>
        )
      })}
    </Grid>
  )
}

WealthboxContactInfoConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default WealthboxContactInfoConfigEditor
